import React, { useContext, useState } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import InputConfig from '../../../../../Components/InputConfig';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import { validatePlatformObj } from '../../../../../utilities/tgmrDocChecks';
import DropDownSingle from '../../../../../Components/DropDownSingle';
import { toast } from 'react-toastify';
import constants from '../../NetworkData/constants.json';

//mui imports====
import { Grid, Paper, Typography, Divider } from '@mui/material';
//===============

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: { PLATFORM_DESKTOP, PLATFORM_MOBILE, AP_BANNERSECTIONID }
} = constants;

const { RESPONSIVE, BANNER_SIZES } = constants.OUTSTREAM_HYBRID_ADS;

const PlatformBannerConfig = ({
	keyData,
	platform,
	desktopAdObj = {},
	mobileAdObj = {},
	isParentEnabled = true
}) => {
	const { rawConfig, updateRawConfig } = useContext(ConfigContext); //rawConfig from ConfigContext
	const bannerSectionIdKeyData = `${keyData}.${AP_BANNERSECTIONID}`;
	const floatingPlayerKey = `formatData.floatingPlayerSizes.${platform}`;
	const resolveDimensions = (size) => {
		if (size === RESPONSIVE) {
			return [
				rawConfig?.[`${floatingPlayerKey}.width`],
				rawConfig?.[`${floatingPlayerKey}.height`]
			];
		}
		const parts = size ? size.split('x') : [];
		return [Number(parts[0]), Number(parts[1])];
	};

	const [selectedBannerSize, setSelectedBannerSize] = useState(() => {
		const w = rawConfig?.[`${keyData}.bannerWidth`];
		const h = rawConfig?.[`${keyData}.bannerHeight`];
		const idx = w && h ? BANNER_SIZES.indexOf(`${w}x${h}`) : -1;
		return idx !== -1 ? BANNER_SIZES[idx] : RESPONSIVE;
	});

	const [bannerWidth, bannerHeight] = resolveDimensions(selectedBannerSize);
	const closeBtnKeyData = `${keyData}.closeBtnPosition`;

	const onBannerSizeChange = (size) => {
		setSelectedBannerSize(size);
		const [width, height] = resolveDimensions(size);
		updateRawConfig((rawObj) => {
			const deepClonedObj = structuredClone(rawObj);
			return {
				...deepClonedObj,
				[`${keyData}.bannerWidth`]: width,
				[`${keyData}.bannerHeight`]: height
			};
		});
	};

	/**
	 * @name onDesktopSectionIdEnabled
	 * @description The function is used to enable the desktop section Id for hybrid ads, it has several conditions which are to be fulfilled to enable the hybrid ad configuration
	 * 1. check desktop Ap Tag object and desktop section Id
	 * @param {Object} enabled - It is an enabled flag whether to enable desktop section in hybrid ads or not
	 */
	const onDesktopSectionIdEnabled = (enabled) => {
		try {
			//1. Validating Desktop Ad object and desktop section Id
			validatePlatformObj(
				desktopAdObj,
				platform,
				rawConfig[bannerSectionIdKeyData],
				PLATFORM_DESKTOP,
				AP_BANNERSECTIONID
			);

			updateRawConfig((rawObj) => {
				const deepClonedObj = structuredClone(rawObj);
				return {
					...deepClonedObj,
					[`${keyData}.enabled`]: enabled,
					[`${keyData}.bannerHeight`]: bannerHeight,
					[`${keyData}.bannerWidth`]: bannerWidth
				};
			});
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error(err);
		}
	};

	/**
	 * @name onMobileSectionIdEnabled
	 * @description The function is used to enable the mobile section Id for hybrid ads, it has several conditions which are to be fulfilled to enable the hybrid ad configuration
	 * 1. check mobile Ap Tag object and mobile section Id
	 * @param {Object} enabled - It is an enabled flag whether to enable mobile section in hybrid ads or not
	 */
	const onMobileSectionIdEnabled = (enabled) => {
		try {
			//1. Validating Mobile Ad object and mobile section Id
			validatePlatformObj(
				mobileAdObj,
				platform,
				rawConfig[bannerSectionIdKeyData],
				PLATFORM_MOBILE,
				AP_BANNERSECTIONID
			);

			updateRawConfig((rawObj) => {
				const deepClonedObj = structuredClone(rawObj);
				return {
					...deepClonedObj,
					[`${keyData}.enabled`]: enabled,
					[`${keyData}.bannerHeight`]: bannerHeight,
					[`${keyData}.bannerWidth`]: bannerWidth
				};
			});
		} catch (err) {
			toast.error(err.message, { position: toast.POSITION.BOTTOM_RIGHT });
			console.error(err);
		}
	};

	return (
		<>
			<Paper
				elevation={3}
				sx={{
					p: 3,
					mt: 3,
					mb: 3,
					borderRadius: 2,
					width: '95%',
					ml: 1,
					backgroundColor: '#fafafa'
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 600,
						mb: 3,
						color: 'primary.main'
					}}
				>
					{platform} Banner Configuration
				</Typography>

				<Grid container spacing={3} alignItems="center">
					{/* Enable + Banner ID */}
					{platform === DESKTOP && (
						<>
							<Grid item xs={12} md={2}>
								<CheckBoxInput
									keyData={`${keyData}.enabled`}
									label="Enabled"
									disabled={isParentEnabled === false}
									defaultChecked={false}
									isObjectEditor={false}
									directValue={rawConfig[`${keyData}.enabled`]}
									updateDirectValue={(enabled) => {
										onDesktopSectionIdEnabled(enabled);
									}}
									containerStyles={{ width: 'fit-content' }}
								/>
							</Grid>

							<Grid item xs={6}>
								<InputConfig
									label="Desktop Banner ID"
									keyData={bannerSectionIdKeyData}
									editable={false}
									isObjectEditor={false}
									directValue={rawConfig[bannerSectionIdKeyData]}
								/>
							</Grid>
						</>
					)}

					{(platform === MOBILE || platform === TABLET) && (
						<>
							<Grid item xs={12} md={2}>
								<CheckBoxInput
									disabled={isParentEnabled === false}
									keyData={`${keyData}.enabled`}
									label="Enabled"
									defaultChecked={false}
									isObjectEditor={false}
									directValue={rawConfig[`${keyData}.enabled`]}
									updateDirectValue={(enabled) => {
										onMobileSectionIdEnabled(enabled);
									}}
									containerStyles={{ width: 'fit-content' }}
								/>
							</Grid>

							<Grid item xs={6}>
								<InputConfig
									label="Mobile Banner ID"
									keyData={bannerSectionIdKeyData}
									editable={false}
									isObjectEditor={false}
									directValue={rawConfig[bannerSectionIdKeyData]}
								/>
							</Grid>
						</>
					)}

					<Grid item xs={12}>
						<Divider />
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<DropDownSingle
							keyData={closeBtnKeyData}
							label="Close Button Position"
							list={['top-right', 'top-left', 'bottom-right', 'bottom-left']}
							formControlVariant="outlined"
							object={rawConfig}
							updateObject={updateRawConfig}
							disabled={!isParentEnabled || !rawConfig[`${keyData}.enabled`]}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<InputConfig
							label="Hide Ad After (ms)"
							keyData={`${keyData}.hideAdAfterMs`}
							inputType="number"
							object={rawConfig}
							updateObject={updateRawConfig}
							editable={isParentEnabled && rawConfig[`${keyData}.enabled`]}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<InputConfig
							label="Banner To Video Wait (ms)"
							keyData={`${keyData}.bannerToVideoWaitMs`}
							inputType="number"
							object={rawConfig}
							updateObject={updateRawConfig}
							editable={isParentEnabled && rawConfig[`${keyData}.enabled`]}
							nullable={true}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<DropDownSingle
							keyData={`${keyData}.bannerSize`}
							label="Banner Size (Width x Height)"
							list={BANNER_SIZES}
							formControlVariant="outlined"
							isObjectEditor={false}
							directValue={selectedBannerSize}
							updateDirectValue={onBannerSizeChange}
							disabled={!isParentEnabled || !rawConfig[`${keyData}.enabled`]}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<InputConfig
							label="Banner Width"
							keyData={`${keyData}.bannerWidth`}
							editable={false}
							isObjectEditor={false}
							directValue={bannerWidth}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<InputConfig
							label="Banner Height"
							keyData={`${keyData}.bannerHeight`}
							editable={false}
							isObjectEditor={false}
							directValue={bannerHeight}
						/>
					</Grid>
				</Grid>
			</Paper>
		</>
	);
};

export default PlatformBannerConfig;
